import { client } from './client'

export const token = process.env.SANITY_API_READ_TOKEN

interface ISanityFetchProps {
    query: string
    params?: Record<string, any>
    tags?: string[]
    isDraftMode?: boolean
}

export async function sanityFetch({ query, params = {}, tags = [], isDraftMode = false }: ISanityFetchProps) {
    return client.fetch(query, params, {
        ...(isDraftMode && {
            token,
            perspective: 'previewDrafts',
        }),
        next: {
            revalidate: isDraftMode ? 0 : false,
            tags,
        },
    })
}

const linkFields = `
    linkType,
    internalLink->{
        ...,
        _type,
        "slug": slug.current,
    },
    href,
    title,
`

const imageFields = `
    ...,
    asset-> {
        url,
        metadata {
            lqip,
        }
    }
`

const r2AssetFields = `
    asset-> {
        contentType,
        fileURL,
    },
`

export const getSiteSettings = async () => {
    const query = `{
        "siteSettings":  *[_type == "siteSettings"][0]{
            ...,
            headerMenu[]->{
                ...,
                page->{
                    title,
                    "slug": slug.current,
                },
                children[]->{
                    title,
                    "slug": slug.current,
                    "description": pageData.metadata.description,
                    pageLogo {
                        ...,
                        asset-> {
                            url,
                        }
                    },
                }
            },
            footerMenu {
                title,
                menuItems[] {
                    ${linkFields}
                }
            },
            menuCta {
                ...,
                link {
                    ${linkFields}
                },
                image {
                    ${imageFields}
                },
            },
            cta {
                ...,
                link {
                    ${linkFields}
                },
                image {
                    ${imageFields}
                },
            },
        }
    }`

    const siteSettingsData = await sanityFetch({
        query,
        tags: ['siteSettings'],
    })

    return siteSettingsData
}

export const modulesFields = `
    modules[] {
        _type,
        ...,
        (_type == "homeHero") => {
            ...,
            video {
                asset-> {
                    url,
                }
            },
            videoFile {
                ${r2AssetFields}
            },
            image {
                ${imageFields}
            },
            featuredProject-> {
                ...,
                title,
                "slug": slug.current,
                projectCategories[]-> {
                    ...,
                    "slug": slug.current,
                },
                featuredImage {
                    ${imageFields}
                },
            },
        },
        (_type == "hero") => {
            ...,
            link {
                ${linkFields}
            },
            image {
                ${imageFields}
            },
        },
        (_type == "cards") => {
            ...,
            cards[] {
                ...,
                link {
                    ${linkFields}
                },
                image {
                    ${imageFields}
                },
            },
            image {
                ${imageFields}
            },
        },
        (_type == "points") => {
            ...,
            link {
                ${linkFields}
            },
        },
        (_type == "cta") => {
            ...,
            link {
                ${linkFields}
            },
            image {
                ${imageFields}
            },
        },
        (_type == "text2Columns") => {
            ...,
            link {
                ${linkFields}
            },
            image {
                ${imageFields}
            },
            video {
                ...,
                videoPoster {
                    ${imageFields}
                },
                video {
                    asset-> {
                        url,
                    }
                },
                videoFile {
                    ${r2AssetFields}
                }
            },
        },
        (_type == "cta2Column") => {
        ...,
            link {
                ${linkFields}
            },
            video {
                ...,
                video {
                    asset-> {
                        url,
                    }
                },
                videoFile {
                    ${r2AssetFields}
                },
            },
            largeVideo {
                ...,
                video {
                    asset-> {
                        url,
                    }
                },
                videoFile {
                    ${r2AssetFields}
                },
            },
            bigLink {
                ...,
                link {
                    ${linkFields}
                },
                image {
                    ${imageFields}
                },
            },
            bigLinkCarousel[] {
                bigLink {
                    ...,
                    link {
                        ${linkFields}
                    },
                    image {
                        ${imageFields}
                    },
                },
            },
        },
        (_type == "servicesList") => {
            ...,
            link {
                ${linkFields}
            },
            services[] {
                ...,
                image {
                    ${imageFields}
                },
            },
        },
        (_type == "graphicDivider") => {
            ...,
            columns[] {
                ...,
                image {
                    ${imageFields}
                },
                video {
                    asset-> {
                        url,
                    }
                },
                videoFile {
                    ${r2AssetFields}
                },
            },
        },
        (_type == "certificationList") => {
            ...,
            link {
                ${linkFields}
            }
        },
        (_type == "servicesHero") => {
            ...,
            link {
                ${linkFields}
            },
        },
        (_type == "largeVideo") => {
            ...,
            video {
                asset-> {
                    url,
                }
            },
            videoFile {
                ${r2AssetFields}
            },
            videoPoster {
                ${imageFields}
            },
        },
        (_type == "blogGrid") => {
            ...,
            featurePost-> {
                ...,
                category->,
            },
        },
        (_type == "projectGrid") => {
            ...,
            featureProject-> {
                ...,
                projectCategories[]->{
                    ...,
                    "slug": slug.current,
                },
                featuredImage {
                    ${imageFields}
                },
            },
        },
        (_type == "list") => {
            ...,
            items[] {
                ...,
                link {
                    ${linkFields}
                },
                download {
                    asset-> {
                        url,
                    },
                },
            },
        },
        (_type == "projectList") => {
            ...,
            link {
                ${linkFields}
            }
        },
        (_type == "teamGrid") => {
            ...,
            rows[] {
                ...,
                members[] {
                    ...,
                    image {
                        ${imageFields}
                    },
                }
            }
        },
        (_type == "gallery") => {
            ...,
            gallery[] {
                ...,
                item {
                    ...,
                    image {
                        ${imageFields}
                    },
                }
            }
        },
        (_type == "communityGrid") => {
            ...,
            stories[] {
                ...,
                image {
                    ${imageFields}
                },
            }
        },
        (_type == "videoGallery") => {
            ...,
            gallery[] {
                ...,
                image {
                    ${imageFields}
                },
                videoFile {
                    ${r2AssetFields}
                },
            },
        },
    }
`
